import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Paragraph } from '@actual-app/components/paragraph';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type {
  SaasPaymentInfo,
  SaasPlanInfo,
  SaasSubscriptionInfo,
} from '@actual-app/core/server/saas/app';

import { Modal, ModalHeader, ModalTitle } from '#components/common/Modal';

type BillingCycle = 'monthly' | 'annual';

function openCheckout(url: string) {
  if (window.Actual?.openURLInBrowser) {
    window.Actual.openURLInBrowser(url);
  } else {
    window.location.href = url;
  }
}

function formatPrice(amount: number, currency: string): string {
  return `${currency} ${amount.toLocaleString()}`;
}

export function SaasSubscriptionModal() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SaasSubscriptionInfo>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [plan, setPlan] = useState<SaasPlanInfo | null>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<SaasPaymentInfo[]>([]);

  async function refresh() {
    setLoading(true);
    const [user, plansResult, historyResult] = await Promise.all([
      send('saas-get-user'),
      send('saas-get-plans'),
      send('saas-get-billing-history'),
    ]);
    setSignedIn(user != null);
    setSubscription(user?.subscription ?? null);
    if (!('error' in plansResult)) {
      setPlan(plansResult.plans[0] ?? null);
      setProviders(plansResult.providers);
    }
    if (!('error' in historyResult)) {
      setPayments(historyResult.payments);
    }
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onSubscribe(provider: string) {
    if (!plan) return;
    setBusy(true);
    setError(null);
    const result = await send('saas-subscribe', {
      planId: plan.id,
      provider,
      billingCycle,
      callbackUrl: window.location.origin + '/subscribe-complete',
    });
    setBusy(false);
    if ('error' in result) {
      setError(result.error);
    } else {
      openCheckout(result.checkoutUrl);
    }
  }

  async function onCancel() {
    if (!subscription) return;
    setBusy(true);
    setError(null);
    const result = await send('saas-cancel-subscription', {
      subscriptionId: subscription.id,
    });
    setBusy(false);
    if ('error' in result) {
      setError(result.error);
    } else {
      await refresh();
    }
  }

  function trialStatus() {
    if (!subscription) return null;
    if (subscription.status === 'trialing' && subscription.trialEndsAt) {
      const endsAt = new Date(subscription.trialEndsAt * 1000);
      const daysLeft = Math.max(
        0,
        Math.ceil((endsAt.getTime() - Date.now()) / 86400000),
      );
      return t('Free trial — {{count}} day(s) left (ends {{date}})', {
        count: daysLeft,
        date: endsAt.toLocaleDateString(),
      });
    }
    if (subscription.status === 'active') {
      if (subscription.canceledAt && subscription.currentPeriodEnd) {
        return t('Active until {{date}} — will not renew', {
          date: new Date(
            subscription.currentPeriodEnd * 1000,
          ).toLocaleDateString(),
        });
      }
      return subscription.currentPeriodEnd
        ? t('Active — renews {{date}}', {
            date: new Date(
              subscription.currentPeriodEnd * 1000,
            ).toLocaleDateString(),
          })
        : t('Active');
    }
    if (subscription.status === 'expired') {
      return t('Your trial has ended');
    }
    return subscription.status;
  }

  const price =
    plan &&
    (billingCycle === 'annual'
      ? formatPrice(plan.priceAnnual, plan.currency) + ' ' + t('per year')
      : formatPrice(plan.priceMonthly, plan.currency) + ' ' + t('per month'));

  // A canceled-at-period-end subscription can be replaced right away, so
  // the subscribe options stay visible for it
  const isPaidActive =
    subscription != null &&
    subscription.status === 'active' &&
    !subscription.canceledAt &&
    subscription.planName !== 'none';

  return (
    <Modal name="saas-subscription">
      {({ state }) => (
        <>
          <ModalHeader
            title={<ModalTitle title={t('Pesa Wise Subscription')} />}
          />
          <View
            style={{
              padding: 15,
              paddingTop: 0,
              paddingBottom: 25,
              gap: 15,
              maxWidth: 512,
              lineHeight: '1.5em',
            }}
          >
            {loading ? (
              <Text style={{ color: theme.pageTextLight }}>
                <Trans>Loading…</Trans>
              </Text>
            ) : !signedIn ? (
              <Paragraph style={{ fontSize: 15 }}>
                <Trans>
                  Sign in with your Pesa Wise account to manage your
                  subscription.
                </Trans>
              </Paragraph>
            ) : (
              <>
                {subscription && (
                  <Text style={{ fontSize: 15, fontWeight: 600 }}>
                    {trialStatus()}
                  </Text>
                )}

                {plan && !isPaidActive && (
                  <>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <Button
                        variant={
                          billingCycle === 'monthly' ? 'primary' : 'normal'
                        }
                        onPress={() => setBillingCycle('monthly')}
                      >
                        <Trans>Monthly</Trans>
                      </Button>
                      <Button
                        variant={
                          billingCycle === 'annual' ? 'primary' : 'normal'
                        }
                        onPress={() => setBillingCycle('annual')}
                      >
                        <Trans>Annual (2 months free)</Trans>
                      </Button>
                    </View>

                    <Text style={{ fontSize: 20, ...styles.tnum }}>
                      {price}
                    </Text>

                    {providers.length === 0 ? (
                      <Paragraph
                        style={{ color: theme.warningText, fontSize: 14 }}
                      >
                        <Trans>
                          No payment providers are configured on this server
                          yet, so checkout is unavailable.
                        </Trans>
                      </Paragraph>
                    ) : (
                      <View style={{ gap: 8 }}>
                        {providers.map(provider => (
                          <ButtonWithLoading
                            key={provider}
                            variant="primary"
                            isLoading={busy}
                            onPress={() => onSubscribe(provider)}
                          >
                            <Trans>Subscribe with {{ provider }}</Trans>
                          </ButtonWithLoading>
                        ))}
                      </View>
                    )}
                  </>
                )}

                {isPaidActive && (
                  <ButtonWithLoading isLoading={busy} onPress={onCancel}>
                    <Trans>Cancel subscription</Trans>
                  </ButtonWithLoading>
                )}

                {payments.length > 0 && (
                  <View style={{ gap: 5 }}>
                    <Text
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.pageTextLight,
                        marginTop: 5,
                      }}
                    >
                      <Trans>Payment history</Trans>
                    </Text>
                    {payments.map(payment => (
                      <View
                        key={payment.id}
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          gap: 10,
                          fontSize: 13,
                        }}
                      >
                        <Text style={{ color: theme.pageTextLight }}>
                          {new Date(
                            payment.createdAt * 1000,
                          ).toLocaleDateString()}
                        </Text>
                        <Text style={styles.tnum}>
                          {formatPrice(payment.amount, payment.currency)}
                        </Text>
                        <Text style={{ color: theme.pageTextLight }}>
                          {payment.provider}
                        </Text>
                        <Text
                          style={{
                            color:
                              payment.status === 'completed'
                                ? theme.noticeTextLight
                                : theme.pageTextLight,
                          }}
                        >
                          {payment.status}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}

                {error && (
                  <Text style={{ color: theme.errorText, fontSize: 14 }}>
                    {t('Something went wrong: {{error}}', { error })}
                  </Text>
                )}
              </>
            )}

            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <Button onPress={() => state.close()}>
                <Trans>Close</Trans>
              </Button>
            </View>
          </View>
        </>
      )}
    </Modal>
  );
}
